import { useContext, useEffect, useState } from "react";
import { storage } from "../firebase";
import { ref, getDownloadURL } from "firebase/storage";
import Header from "./Header";
import Footer from "./Footer";
import ImageGallery from "./ImageGallery";
import { HeroImageWithSkeleton } from "./ImageSkeleton";
import LanguageContext from "./contexts/languageContext";
import { Helmet } from "react-helmet-async";

const translations = {
  en: {
    title: "Present Continuous",
    subTitle: "The paradox of relationships: broken and connected",
    year: "2018",
    material: "oil on canvas",
    size: "F10. 455mm x 530mm",
    description: `The wounds of human relationships are often not as easily
                  healed as the wounds of giving our hearts to others
                  Paradoxically, it is only through other relationships, not
                  isolation or disconnection, that the shards of our wounded
                  souls can be healed. The primal, vital images that fill the
                  screen are organized like cells, and the figures on the canvas
                  can represent people in themselves. It is not only the figure
                  of a person, but also a network of relationships that
                  surrounds him. Not only the composition, but also the color
                  scheme shows the anxiety and reflection on human
                  relationships. The repeated overpainting of different intense
                  colors is also a reflection of the repeated moments of
                  encounters and conflicts between people, which are layered on
                  top of each other. In the series, the lines that are torn
                  apart with pain and then reunited with life are continuously
                  connected by lines that are full of vitality, signifying the
                  “connectivity” of relationships that cannot be completely
                  isolated. The artist conveys the idea that even though human
                  relationships are divided by various causes and are parallel
                  lines that can never be me, the relationship with others is an
                  unbreakable link and is indispensable to the existence of the
                  world like a cell.`,
  },
  ko: {
    title: "현재진행형",
    subTitle: "관계의 역설: 끊기고도 이어진",
    year: "2024",
    material: "캔버스에 유채",
    size: "F10. 455mm x 530mm",
    description: `타인에게 마음을 주었던 만큼 인간관계에서 다친 상처는 쉽게 아물지 않곤 한다. 역설적으로 상처 입은 영혼의 파편이 치유될 수 있는 것은 고립이나 단절이 아닌 또 다른 관계를 통해서 비로소 가능하다. 화면 가득 채워진 원초적이고 생명적인 이미지는 세포처럼 조직망을 갖고 있고 화폭 위 형체는 그 자체로서 사람을 의미할 수 있다.
한 사람의 형체인 동시에 그를 둘러싼 여러 관계들이 얽히고설킨 조직망으로 이루어진 군상인 것이다. 구성뿐만 아니라 색채에서도 사람 관계에 대한 번민과 성찰이 담겨있다.
서로 다른 강렬한 색상이 여러 번 덧칠해지기를 반복한 것 역시 계속해서 반복되는 사람 간의 만남과 갈등의 순간이 겹겹이 더해진 것이라 할 수 있다. 연작에서 고통을 감내하며 찢기듯이 갈라지다 다시금 생명력을 품은 선들로 연속적으로 이어지고 이는 완벽히 고립될 수 없는 관계의 ‘연결성’을 의미한다. 인간관계 역시 다양한 원인에 의해 균열이 생기고 절대 내가 될 수 없는 평행선일지라도 타자와 관계는 끊을 수 없는 고리이며 세포와도 같이 세상의 존립에 필수불가결한 존재임을 화폭에 담았다.`,
  },
};

const getFontFamily = (language: "en" | "ko") => {
  return language === "ko"
    ? '"pretendard", sans-serif'
    : '"neue-haas-grotesk-display", sans-serif';
};

const ArtworkPage = () => {
  const [heroImageUrl, setHeroImageUrl] = useState("");

  const context = useContext(LanguageContext);

  if (!context) {
    throw new Error("ArtworkPage must be used within a LanguageProvider");
  }

  const { language } = context;
  const content = translations[language];

  // Common text style with dynamic font family
  const textStyle = {
    fontFamily: getFontFamily(language),
    fontWeight: language === "ko" ? 600 : 800,
    fontStyle: "normal",
  };

  const titleStyle = {
    fontFamily: getFontFamily(language),
    fontWeight: 800, // 한국어/영어 모두 800 weight 적용
    fontStyle: "normal",
  };

  const subTitleStyle = {
    fontFamily: getFontFamily(language),
    fontWeight: 700,
    fontStyle: "normal",
  };

  // 히어로 이미지와 갤러리 이미지 URL을 가져오는 함수
  useEffect(() => {
    const loadImages = async () => {
      try {
        const heroRef = ref(storage, "artwork/2018-40.webp");
        console.log("Hero ref:", heroRef);
        const heroUrl = await getDownloadURL(heroRef);
        console.log("Hero URL:", heroUrl);
        setHeroImageUrl(heroUrl);

        console.log("All images loaded successfully");
      } catch (error) {
        console.error("Error loading images:", error);
        if (error instanceof Error) {
          console.error("Error details:", {
            message: error.message,
            name: error.name,
            stack: error.stack,
          });
        }
      }
    };

    loadImages();
  }, []);

  return (
    <>
      <Helmet>
        <title>Artwork | 작품소개 </title>
        <meta
          name="description"
          content="이선희 작가의 다양한 작품을 소개하는 갤러리 페이지입니다."
        />
      </Helmet>
      <div className="relative min-h-screen bg-white">
        <Header />
        <main className="pt-20">
          {/* Use grid for better control over layout */}
          <div className="grid md:grid-cols-5 min-h-screen gap-4">
            {/* Left Column - Artwork Information */}
            <div className="md:col-span-2 p-4 md:p-8 lg:p-14">
              {/* Create a wrapper for sticky content */}
              <div className="md:sticky md:top-20 md:max-h-[calc(100vh-5rem)] md:overflow-y-auto scrollbar-hide">
                <div className="space-y-6 pt-4">
                  <h1
                    className="text-4xl sm:text-5xl md:text-6xl lg:text-8xl font-bold"
                    style={titleStyle}
                  >
                    {content.title}
                  </h1>
                  <h2
                    className="text-2xl md:text-4xl font-bold text-gray-700"
                    style={subTitleStyle}
                  >
                    {content.subTitle}
                  </h2>
                  <p
                    className="text-5xl md:text-7xl text-gray-300"
                    style={{
                      fontFamily: '"neue-haas-grotesk-display", sans-serif',
                      fontWeight: 800,
                      fontStyle: "normal",
                    }}
                  >
                    {content.year}
                  </p>
                  <div className="space-y-4 mt-20">
                    <p className="text-base" style={titleStyle}>
                      {content.material}
                    </p>
                    <p className="text-base" style={titleStyle}>
                      {content.size}
                    </p>
                    <p
                      className="mt-8 text-base leading-relaxed pb-8"
                      style={textStyle}
                    >
                      {content.description}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            {/* Right Column - Image */}
            <div className="md:col-span-3">
              <div className="sticky top-20">
                <HeroImageWithSkeleton
                  imageUrl={heroImageUrl}
                  alt="Present Continuous 46, 2024"
                  className="w-full h-auto"
                />
              </div>
            </div>
          </div>
          {/* Gallery Section - Now outside the main grid */}
          <div className="w-full mt-20 clear-both">
            <ImageGallery />
          </div>
        </main>
        <footer className="relative w-full">
          <Footer />
        </footer>
      </div>
    </>
  );
};

export default ArtworkPage;
